import React, { useMemo } from 'react';
import { Grid, Row, FlexboxGrid, Divider } from 'rsuite';
import { useSelector } from 'react-redux';
import {
  selectConfigExpressTax,
  selectConfigExpressAllTaxCollectionAgreementWith,
} from '../../../store/slices/config/configSelectors';
import { TableComponent } from '../../common/TableComponent';
import { getDateRelatedBucket } from '../../../common/utils';
import { TaxBreakdownProvider } from './TaxBreakdownProvider';
import { TaxBreakdownItem } from './TaxBreakdownItem';

function getSummaryDataFromLicense(license, periodLabel, buckets) {
  const summaryRows = license.map((provider) => {
    // all totDue values
    const totDueSum = Object.values(provider.taxTypes || {}).reduce(
      (sum, taxType) => sum + (taxType.totDue || 0),
      0
    );

    const bucket = buckets.find((b) => b.key === provider.key);
    const label = bucket?.label
      ? `Tax Due for ${bucket.label}`
      : `Tax Due for ${provider.key}`;
    return {
      label,
      value: `$${totDueSum.toFixed(2)}`,
    };
  });

  // calculate total
  const total = summaryRows.reduce(
    (sum, row) => sum + parseFloat(row.value.replace('$', '')),
    0
  );

  summaryRows.push({
    label: `Total Due for ${periodLabel}`,
    value: `$${total.toFixed(2)}`,
    bold: true,
  });

  return summaryRows;
}

const TaxPayableBreakdownTable = ({
  period,
  license,
  periodLabel,
  processingFee = null,
  totalPayment = null,
}) => {
  const allBuckets = useSelector(
    selectConfigExpressAllTaxCollectionAgreementWith
  );
  const buckets = useMemo(() => {
    return getDateRelatedBucket(allBuckets, period?.startDate);
  }, [allBuckets, period]);

  const taxConfiguration = useSelector(selectConfigExpressTax);
  const summaryData = useMemo(() => {
    return getSummaryDataFromLicense(license, periodLabel, buckets);
  }, [license, periodLabel, buckets]);

  return (
    <Grid fluid>
      <FlexboxGrid
        as={Row}
        align="bottom"
      >
        <FlexboxGrid.Item colspan={24}>
          <TableComponent
            data={summaryData}
            title="Summary"
          />
        </FlexboxGrid.Item>
        <FlexboxGrid.Item colspan={24}>
          <Divider />
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <FlexboxGrid as={Row}>
        {license.map((provider) => {
          const bucket = buckets.find((b) => b.key === provider.key);
          if (!bucket) return <></>;
          return (
            <FlexboxGrid.Item
              key={provider.key}
              as={Row}
              colspan={24}
            >
              <TaxBreakdownProvider
                taxableActivity={provider}
                configuration={taxConfiguration}
                bucket={bucket}
              />
            </FlexboxGrid.Item>
          );
        })}
      </FlexboxGrid>
      <FlexboxGrid
        as={Row}
        colspan={24}
      >
        {processingFee !== null && (
          <FlexboxGrid.Item colspan={24}>
            <FlexboxGrid
              as={Row}
              style={{ margin: '8px 0' }}
            >
              <TaxBreakdownItem
                title="Processing Fee"
                value={processingFee}
              />
            </FlexboxGrid>
          </FlexboxGrid.Item>
        )}

        {totalPayment !== null && (
          <FlexboxGrid.Item colspan={24}>
            <FlexboxGrid
              as={Row}
              style={{ margin: '0' }}
            >
              <TaxBreakdownItem
                title={`Total Payment Due ${periodLabel}`}
                value={totalPayment}
                bold
              />
            </FlexboxGrid>
          </FlexboxGrid.Item>
        )}
      </FlexboxGrid>
    </Grid>
  );
};

export default TaxPayableBreakdownTable;
